<script setup>

import { Head, Link, useForm } from '@inertiajs/inertia-vue3';
import {  watch, ref } from 'vue';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import Multiselect from "@vueform/multiselect";

const props = defineProps({
    email: '',
    version: '',
    countries: Object,
    tail: '',
    path: '',
});

const form = useForm({
    tail: '',
    country_id: 1,
    name: '',
    last_name: '',
    email: props.email ? props.email : '',
    password: '',
    password_confirmation: '',
    terms: false,
    version: props.version,
    engineType: 'Piston',
    numberOfEngines: 1,
});

const submit = () => {
    let url = props.path === '' || props.path === undefined ? route('register') : props.path;

    if (form.country_id === 1 && !/^\d/.test(form.tail) && !invitation.value) {
        form.setError('tail', 'US tail numbers must begin with a number.')
        return
    }

    form.post(url, {
        onFinish: () => form.reset('password', 'password_confirmation'),
    });
};

const invitation = ref(false);

watch(() => props.path, (newVal, oldVal) => {
    if(typeof newVal !== 'undefined' && newVal !== ''){
        invitation.value = true;
    }
},
    { immediate: true });

</script>
<style >
@import '#/@vueform/multiselect/themes/tailwind.css';
</style>

<template>
    <Head title="Register" />

    <AuthenticationCard>
        <template #logo>
            <AuthenticationCardLogo />
        </template>

        <form @submit.prevent="submit">
            <div class="relative flex grid rows" v-if="!invitation">
                <InputLabel for="text" value='Country Tail Prefix ("N")' />
                <Multiselect
                    class="mt-1 block w-full rounded-md border-0 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    :class="{
                        options: 'flex flex-col p-0 m-0 list-none text-black',
                        option: 'bg-logix-dark-blue',
                    }"
                    :searchable="true"
                    v-model="form.country_id"
                    placeholder="Type to search"
                    :options="countries"
                    label="tail_prefix"
                    value-prop="id"
                    :autofocus="!invitation"
                >
                    <template v-slot:option="{ option }">
                        <p class="w-full">{{ option.tail_prefix }} - {{option.country_name}}</p>
                    </template>
                </Multiselect>
                <InputError :message="form.errors.country" class="mt-2" />
            </div>

            <div class="relative flex grid rows mt-4" v-if="!invitation">

                <InputLabel for="tail" value='Aircraft Tail (Exclude country prefix like "N" or "C")' />
                <TextInput
                    id="tail"
                    v-model="form.tail"
                    type="text"
                    class="mt-1 block w-full"
                    required
                />

                <InputError :message="form.errors.tail" class="mt-2" />
            </div>
            <div class="col-span-6 sm:col-span-3 mt-4" v-if="!invitation">
                <label for="engineType" class="block text-sm font-medium text-gray-700">Engine Type</label>
                <select v-model="form.engineType" id="engineType" name="engineType" class="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-logix-green focus:outline-none focus:ring-logix-green sm:text-sm">
                    <option selected>Piston</option>
                    <option>Turboprop</option>
                    <option>Jet</option>
                    <option>Electric</option>
                </select>
            </div>
            <div class="mt-4" v-if="!invitation">
                <InputLabel for="numberOfEngines" value="# of Engines"/>
                <TextInput
                    id="ac_ttaf"
                    ref="aircraftModelInput"
                    v-model="form.numberOfEngines"
                    type="number"
                    max="4"
                    min="1"
                />
                <InputError class="mt-2"/>
            </div>

            <div class=" mt-4" v-if="invitation">
                Invitation for Tail <strong>{{props.tail}}</strong> found! Register to create an account and accept.
            </div>

            <div class="mt-4">
                <InputLabel for="name" value="First Name" />
                <TextInput
                    id="name"
                    v-model="form.name"
                    type="text"
                    class="mt-1 block w-full"
                    required
                    :autofocus="invitation"
                    autocomplete="name"
                />
                <InputError class="mt-2" :message="form.errors.name" />
            </div>
            <div class="mt-4">
                <InputLabel for="last_name" value="Last Name" />
                <TextInput
                    id="last_name"
                    v-model="form.last_name"
                    type="text"
                    class="mt-1 block w-full"
                    required
                    :autofocus="invitation"
                    autocomplete="last_name"
                />
                <InputError class="mt-2" :message="form.errors.last_name" />
            </div>

            <div class="mt-4">
                <InputLabel for="email" value="Email" />
                <TextInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                    required
                />
                <InputError class="mt-2" :message="form.errors.email" />
            </div>

            <div class="mt-4">
                <InputLabel for="password" value="Password" />
                <TextInput
                    id="password"
                    v-model="form.password"
                    type="password"
                    class="mt-1 block w-full"
                    required
                    autocomplete="new-password"
                />
                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div class="mt-4">
                <InputLabel for="password_confirmation" value="Confirm Password" />
                <TextInput
                    id="password_confirmation"
                    v-model="form.password_confirmation"
                    type="password"
                    class="mt-1 block w-full"
                    required
                    autocomplete="new-password"
                />
                <InputError class="mt-2" :message="form.errors.password_confirmation" />
            </div>

            <div v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature" class="mt-4">
                <InputLabel for="terms">
                    <div class="flex items-center">
                        <Checkbox id="terms" v-model:checked="form.terms" name="terms" required />

                        <div class="ml-2">
                            I agree to the <a target="_blank" :href="route('terms.show')" class="underline text-sm text-gray-600 hover:text-gray-900">Terms of Service and Privacy Policy</a>
                        </div>
                    </div>
                    <InputError class="mt-2" :message="form.errors.terms" />
                </InputLabel>
            </div>

            <div class="flex items-center justify-end mt-4">
                <Link :href="route('login')" class="underline text-sm text-gray-600 hover:text-gray-900">
                    Already registered?
                </Link>

                <PrimaryButton class="ml-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Register
                </PrimaryButton>
            </div>
        </form>
    </AuthenticationCard>
</template>
